import { DocumentStatus, TerminationReason } from '../../generated/graphql';
import {
  getEventLabelFromMeta,
  trackTerminatedAssessment,
} from '../../helpers/analytics';
import { MinorPolicyHolderRelationLabelValueMap } from '../../helpers/assessment';
import { isAtLeast18YearsOld } from '../../helpers/patient';
import { getIntakeStatesCovered } from '../../helpers/states';
import { updateDocument } from '../api-wrappers';
import {
  selectMedicalInfoAnswers,
  selectPatient,
  selectPolicyholderDetails,
  selectReasonVisitAnswers,
} from '../assessment';
import { selectExperiments } from '../experiments';
import { hasOtherPlan, isPlanOutOfNetwork } from '../helpers/assessment';
import { selectLdFlags } from '../ld-flags';
import { selectMatching } from '../matching';
import {
  isInReview,
  selectHasPreviouslyCompletedAssessment,
  selectPresentMeta,
} from '../meta';
import { selectIsGuardian, selectReferralSource, selectUser } from '../session';
import {
  GetTarget,
  MetaTargetConfig,
  ReferralSource,
  RootState,
  Screens,
} from '../types';

export const sendTermination = (
  terminationReason: TerminationReason,
  state: RootState
) => {
  const meta = selectPresentMeta(state);
  const eventLabel = getEventLabelFromMeta(meta);
  trackTerminatedAssessment(eventLabel);

  const documentInput = {
    status: DocumentStatus.Completed,
    terminationReason,
  };
  void updateDocument(documentInput);
};

const getGetStartedTarget: GetTarget = state => {
  const { showConsentReferrerSharing } = selectLdFlags(state);

  if (selectUser(state)) {
    if (selectHasPreviouslyCompletedAssessment(state))
      return Screens.PreviouslyCompleted;
    const referralSource = selectReferralSource(state);
    if (
      showConsentReferrerSharing &&
      !!referralSource &&
      referralSource !== ReferralSource.Zocdoc
    )
      return Screens.ConsentReferrerSharing;
    return Screens.PatientType;
  }
  return Screens.CollectEmail;
};

const getZocdocIntroductionSecondTarget = getGetStartedTarget;

const getLandingRouterTarget: GetTarget = state => {
  const {
    skipIntroAfterAuth,
    shouldHideIntroScreenOne,
    shouldHideIntroScreenTwo,
    shouldHideIntroScreenThree,
  } = selectLdFlags(state);

  const { ab_test_intro_screens } = selectExperiments(state);

  const referralSource = selectReferralSource(state);
  if (skipIntroAfterAuth && !referralSource && selectUser(state)) {
    return getGetStartedTarget(state);
  }
  if (referralSource === ReferralSource.Zocdoc)
    return Screens.ZocdocIntroductionFirst;

  if (!referralSource) {
    if (!shouldHideIntroScreenOne && !ab_test_intro_screens)
      return Screens.IntroScreenOne;
    if (!shouldHideIntroScreenTwo && !ab_test_intro_screens)
      return Screens.IntroScreenTwo;
    if (!shouldHideIntroScreenThree && !ab_test_intro_screens)
      return Screens.IntroScreenThree;

    return Screens.IntroScreenFour;
  }
  return Screens.Introduction;
};

const getIntroScreenOneTarget: GetTarget = state => {
  const { shouldHideIntroScreenTwo, shouldHideIntroScreenThree } =
    selectLdFlags(state);

  if (!shouldHideIntroScreenTwo) {
    return Screens.IntroScreenTwo;
  }

  if (!shouldHideIntroScreenThree) {
    return Screens.IntroScreenThree;
  }

  return Screens.IntroScreenFour;
};

const getIntroScreenTwoTarget: GetTarget = state => {
  const { shouldHideIntroScreenThree } = selectLdFlags(state);

  if (!shouldHideIntroScreenThree) {
    return Screens.IntroScreenThree;
  }

  return Screens.IntroScreenFour;
};

const getIntroScreenThreeTarget: GetTarget = state => {
  const { ab_test_intro_screens } = selectExperiments(state);

  if (ab_test_intro_screens) {
    return Screens.CollectEmail;
  }
  return Screens.IntroScreenFour;
};

const getIntroScreenFourTarget: GetTarget = state => {
  const { ab_test_intro_screens } = selectExperiments(state);

  if (ab_test_intro_screens) {
    return Screens.IntroScreenThree;
  }
  return Screens.CollectEmail;
};

const getPatientTypeTarget: GetTarget = state => {
  const { shouldUseIntakeV2Point1 } = selectLdFlags(state);

  if (shouldUseIntakeV2Point1) {
    return Screens.PersonalDetails;
  }

  return Screens.CollectDemographics;
};

const getCollectDemographicsTarget: GetTarget = state => {
  const { addressState, birthDate } = selectPatient(state);
  const { listStatesSupported } = selectLdFlags(state);
  const intakeStatesCovered = getIntakeStatesCovered(listStatesSupported);
  const isGuardian = selectIsGuardian(state);

  if (!intakeStatesCovered.includes(addressState)) {
    sendTermination(TerminationReason.StateNotCovered, state);
    return Screens.OutOfCoverage;
  }

  const isUnder18YearsOld = !isAtLeast18YearsOld(birthDate);
  if (isUnder18YearsOld && isGuardian === false) {
    sendTermination(TerminationReason.CompletedByMinor, state);
    return Screens.CompletedByMinorTermination;
  }

  const isOver18YearsOld = !isUnder18YearsOld;
  if (isOver18YearsOld && isGuardian === true) {
    sendTermination(TerminationReason.NotAMinor, state);
    return Screens.NotAMinorTermination;
  }

  if (isInReview(state)) return Screens.Review;

  if (isGuardian && isUnder18YearsOld) return Screens.ConsentForMinor;

  const { shouldUseIntakeV2Point1 } = selectLdFlags(state);
  if (shouldUseIntakeV2Point1) return Screens.RV_MainReason;

  const { show_insurance_combo_screen } = selectExperiments(state);
  if (show_insurance_combo_screen) return Screens.MI_InsuranceCombo;
  return Screens.MI_InsuranceForm;
};

const getConsentReferrerSharingTarget: GetTarget = state => {
  const isGuardian = selectIsGuardian(state);
  if (isGuardian) return Screens.ConsentForMinor;
  return Screens.PatientType;
};

const getPersonalDetailsTarget: GetTarget = state => {
  const { shouldUseIntakeV2Point1 } = selectLdFlags(state);
  if (shouldUseIntakeV2Point1) {
    return Screens.StateCheck;
  }

  return Screens.CollectDemographics;
};

const getStateCheckTarget: GetTarget = state => {
  const { show_insurance_combo_screen } = selectExperiments(state);
  if (show_insurance_combo_screen) return Screens.MI_InsuranceCombo;
  return Screens.MI_InsuranceForm;
};

const getGuardianDemographicsTarget: GetTarget = state => {
  if (isInReview(state)) {
    // If the patient updated the guardians and the policyholder
    // is a guardian, we need them to confirm the policyholder again
    const policyholder = selectPolicyholderDetails(state);
    const hasGuardianPolicyholder =
      !policyholder ||
      policyholder?.relationToPatient ===
        MinorPolicyHolderRelationLabelValueMap['Secondary guardian'] ||
      policyholder?.relationToPatient ===
        MinorPolicyHolderRelationLabelValueMap['Primary guardian'];
    if (hasGuardianPolicyholder) return Screens.MI_PrimaryPolicyHolder;

    return Screens.Review;
  }

  const { shouldUseIntakeV2Point1 } = selectLdFlags(state);
  if (shouldUseIntakeV2Point1) return Screens.RV_MainReason;

  const { show_insurance_combo_screen } = selectExperiments(state);
  if (show_insurance_combo_screen) return Screens.MI_InsuranceCombo;
  return Screens.MI_InsuranceForm;
};

const getRV_SecondaryReasonTarget: GetTarget = state => {
  const { shouldUseIntakeV2Point1 } = selectLdFlags(state);
  const { RV_SecondaryReason } = selectReasonVisitAnswers(state);
  if (shouldUseIntakeV2Point1) {
    let reasonSchizophrenia = false;

    // TODO: fixup after 2.1 is released
    // eslint-disable-next-line no-restricted-syntax
    for (const reason of RV_SecondaryReason) {
      if (reason.includes('Schizophrenia')) {
        reasonSchizophrenia = true;
        break;
      }
    }
    if (reasonSchizophrenia) {
      sendTermination(TerminationReason.Schizophrenic, state);
      return Screens.NoService;
    }
  }

  if (RV_SecondaryReason.includes('Schizophrenia')) {
    sendTermination(TerminationReason.Schizophrenic, state);
    return Screens.NoService;
  }
  return Screens.RV_SpecialtyCare;
};

const getRV_PsychiatricInpatientTarget: GetTarget = state => {
  const { RV_PsychiatricImpatient } = selectReasonVisitAnswers(state);
  if (RV_PsychiatricImpatient.includes('Yes')) {
    sendTermination(TerminationReason.RecentlyInpatient, state);
    return Screens.NoService;
  }
  return Screens.RV_KillingYourself;
};

const getRV_KillingYourselfTarget: GetTarget = state => {
  const { RV_KillingYourself } = selectReasonVisitAnswers(state);
  if (RV_KillingYourself.includes('Yes')) {
    sendTermination(TerminationReason.Emergency, state);
    return Screens.Emergency;
  }
  return Screens.RV_DrinkingAlcohol;
};

const getRV_DrinkingAlcoholTarget: GetTarget = state => {
  if (isInReview(state)) return Screens.Review;
  return Screens.RV_UseOfDrugs;
};

const getRV_UseOfDrugsTarget: GetTarget = state => {
  if (isInReview(state)) return Screens.Review;
  return Screens.MI_MedicationPharmacyCombo;
};

const getMI_InsuranceFormTarget: GetTarget = state => {
  const { MI_InsurancePlan } = selectMedicalInfoAnswers(state);

  const outOfNetworkPlan = isPlanOutOfNetwork(MI_InsurancePlan);
  const otherPlan = hasOtherPlan(MI_InsurancePlan);
  const hasOONProvider = !MI_InsurancePlan?.length;

  if (hasOONProvider || outOfNetworkPlan || otherPlan) {
    if (hasOONProvider)
      sendTermination(TerminationReason.PayorOutOfNetwork, state);
    else if (outOfNetworkPlan)
      sendTermination(TerminationReason.InsurancePlanOutOfNetwork, state);
    return Screens.NotInNetwork;
  }

  return Screens.InNetwork;
};

const getMI_InsuranceIdTarget: GetTarget = state => {
  if (isInReview(state)) return Screens.Review;

  return Screens.MI_PrimaryPolicyHolder;
};

const getMI_InsuranceComboTarget: GetTarget = state => {
  if (isInReview(state)) return Screens.Review;

  const { shouldUseIntakeV2Point1 } = selectLdFlags(state);
  if (shouldUseIntakeV2Point1) return Screens.MedicalInfoIntro;

  return Screens.RV_MainReason;
};

const getMI_PrimaryPolicyHolderTarget: GetTarget = state => {
  if (isInReview(state)) return Screens.Review;

  const { shouldUseIntakeV2Point1 } = selectLdFlags(state);
  if (shouldUseIntakeV2Point1) return Screens.MedicalInfoIntro;

  return Screens.RV_MainReason;
};

const getMI_MedicationPharmacyComboTarget: GetTarget = state => {
  if (isInReview(state)) return Screens.Review;
  return Screens.MI_EmergencyContact;
};

const getMI_EmergencyContactTarget: GetTarget = state => {
  if (isInReview(state)) return Screens.Review;
  const referralSource = selectReferralSource(state);
  if (referralSource === ReferralSource.Zocdoc) return Screens.Review;
  return Screens.MI_HaveAppointment;
};

const getReviewTarget: GetTarget = state => {
  const { appointmentTime, provider, matches } = selectMatching(state);
  const { shouldShowMatchLoadingScreen } = selectLdFlags(state);
  const { MI_HaveAppointment } = selectMedicalInfoAnswers(state);
  const referralSource = selectReferralSource(state);

  if (
    shouldShowMatchLoadingScreen &&
    referralSource !== ReferralSource.Zocdoc &&
    MI_HaveAppointment.includes('No')
  )
    return Screens.MatchLoading;
  if (appointmentTime && provider) return Screens.AppointmentConfirmation;
  if (!matches.length) return Screens.Finish;

  return Screens.List;
};

const getMatchLoadingTarget: GetTarget = state => {
  const { appointmentTime, provider, matches } = selectMatching(state);

  if (appointmentTime && provider) return Screens.AppointmentConfirmation;
  if (!matches.length) return Screens.Finish;

  return Screens.List;
};

const getMatchingListTarget: GetTarget = state => {
  const { provider } = selectMatching(state);
  if (!provider) return Screens.NoMatch;
  return Screens.AppointmentConfirmation;
};

const metaTargetConfig: MetaTargetConfig = {
  [Screens.LandingRouter]: getLandingRouterTarget,
  [Screens.Introduction]: () => Screens.GetStarted,
  [Screens.IntroScreenOne]: getIntroScreenOneTarget,
  [Screens.IntroScreenTwo]: getIntroScreenTwoTarget,
  [Screens.IntroScreenThree]: getIntroScreenThreeTarget,
  [Screens.IntroScreenFour]: getIntroScreenFourTarget,
  [Screens.GetStarted]: getGetStartedTarget,
  [Screens.ZocdocIntroductionFirst]: () => Screens.ZocdocIntroductionSecond,
  [Screens.ZocdocIntroductionSecond]: getZocdocIntroductionSecondTarget,
  [Screens.CollectEmail]: () => Screens.ConfirmEmail,
  [Screens.PreviouslyCompleted]: () => Screens.PatientType,
  [Screens.PatientType]: getPatientTypeTarget,
  [Screens.PersonalDetails]: getPersonalDetailsTarget,
  [Screens.StateCheck]: getStateCheckTarget,
  [Screens.ConsentForMinor]: () => Screens.GuardianDemographics,
  [Screens.MedicalInfoIntro]: () => Screens.CollectDemographics,
  [Screens.GuardianDemographics]: getGuardianDemographicsTarget,
  [Screens.CollectDemographics]: getCollectDemographicsTarget,
  [Screens.ConsentReferrerSharing]: getConsentReferrerSharingTarget,
  [Screens.RV_MainReason]: () => Screens.RV_SecondaryReason,
  [Screens.RV_SecondaryReason]: getRV_SecondaryReasonTarget,
  [Screens.RV_SpecialtyCare]: () => Screens.SensitiveInformationWarning,
  [Screens.SensitiveInformationWarning]: () => Screens.RV_PsychiatricImpatient,
  [Screens.RV_PsychiatricImpatient]: getRV_PsychiatricInpatientTarget,
  [Screens.RV_KillingYourself]: getRV_KillingYourselfTarget,
  [Screens.RV_DrinkingAlcohol]: getRV_DrinkingAlcoholTarget,
  [Screens.RV_UseOfDrugs]: getRV_UseOfDrugsTarget,
  [Screens.MI_InsuranceCombo]: getMI_InsuranceComboTarget,
  [Screens.MI_InsuranceForm]: getMI_InsuranceFormTarget,
  [Screens.MI_InsuranceId]: getMI_InsuranceIdTarget,
  [Screens.NotInNetwork]: () => Screens.MI_InsuranceId,
  [Screens.InNetwork]: () => Screens.MI_InsuranceId,
  [Screens.MI_PrimaryPolicyHolder]: getMI_PrimaryPolicyHolderTarget,
  [Screens.MI_MedicationPharmacyCombo]: getMI_MedicationPharmacyComboTarget,
  [Screens.MI_EmergencyContact]: getMI_EmergencyContactTarget,
  [Screens.MI_HaveAppointment]: () => Screens.Review,
  [Screens.Review]: getReviewTarget,
  [Screens.MatchLoading]: getMatchLoadingTarget,
  [Screens.List]: getMatchingListTarget,
};

export default metaTargetConfig;
